import { useId, useMemo, useState, type KeyboardEvent } from 'react'
import { Check, ChevronDown } from 'lucide-react'
import { EntityIcon } from '../EntityIcon'
import { matchesText } from '../textSearch'
import { cx } from './cx'
import { isSelectable, UNAVAILABLE_MARKER } from './Select'
import { normalizeTone, resolveOption, type DecoratedOption } from './optionSet'
import s from './OptionPicker.module.css'

/**
 * ONE single-select combobox for a registered option set, at EVERY length (#1466 row 6).
 *
 * ── What row 6 asks for, and the fork it forbids ────────────────────────────
 * A long registered option set must be SEARCHABLE. The trap is answering that
 * by making a long set a DIFFERENT control: a combobox here and a select there
 * is two controls for one job, and a person who has learned one meets the other
 * with no warning. So nothing in this file branches on `options.length`. Three
 * options and thirty behave identically — the filter simply matches everything
 * until you type, because `matchesText('')` is true by construction.
 *
 * ── The attempt this replaces, and the two measurements that killed it ──────
 * The first answer put a filter `<input>` INSIDE the Radix `Select` menu. Both
 * of these were measured against the installed `@radix-ui/react-select@2.3.2`
 * and both are re-checkable in `node_modules`:
 *
 *   1. `role="listbox"` is on the CONTENT element — `dist/index.mjs:480`
 *      renders `SelectPosition` with `role: "listbox"`. So the filter was a
 *      `textbox` CHILD OF A LISTBOX, which is not a permitted structure: an AT
 *      walking that subtree is told the input is one of the options.
 *   2. `focusSelectedItem()` runs from a `useEffect` on `isPositioned`
 *      (`dist/index.mjs:348-356`), i.e. AFTER mount, so it overrode the
 *      filter's `autoFocus`. The filter never held focus on open: a keyboard
 *      user landed on an option and their next keystroke went to Radix's
 *      type-ahead. The feature was mouse-only.
 *
 * A combobox has neither problem, because the textbox IS the control and the
 * listbox is a sibling it points at with `aria-controls`. That is why this is a
 * new component rather than a patch.
 *
 * ── Sibling to MultiSelect, plus the one thing MultiSelect does not do ──────
 * The contract is deliberately `MultiSelect`'s: `role="combobox"` +
 * `aria-expanded` + `aria-autocomplete="list"` + `aria-controls`, a
 * `role="listbox"` of `role="option"` children, arrow/Enter/Escape keys, and
 * `selectable === false` rendered-but-closed. Two controls, one grammar.
 *
 * What it adds is `aria-activedescendant`. `MultiSelect` tracks a highlight
 * index and scrolls to it but never announces it, so an arrow key moves a
 * highlight no screen-reader user is told about — the list is navigable and
 * silent. `CommandPalette` has the complete pattern and this file follows it,
 * including the `${listId}-option-${index}` id scheme.
 *
 * Two smaller consequences of the same reasoning:
 *   - the listbox id comes from `useId()`, not from the caller's `id`.
 *     `MultiSelect` derives it from `id` and therefore emits
 *     `aria-controls={undefined}` when the caller passes none, so an
 *     unlabelled MultiSelect is a combobox that says it controls nothing.
 *   - the "nothing matches" line sits OUTSIDE the `role="listbox"` element. A
 *     listbox may own options and groups; a bare div inside one is the same
 *     class of defect as the textbox above, one size smaller.
 *
 * ── Decoration is governed, and it may only ever ADD ────────────────────────
 * `tone` and `icon` resolve exactly as they do for `Select` and `OptionValue`,
 * through `optionSet.ts`: `tone` is clamped to `Pill`'s five and anything else
 * reads neutral, `icon` is an `EntityIcon` registry key. Neither is CSS and
 * neither may be a colour — an undeclared token paints nothing and does it
 * silently.
 *
 * ── The name is the caller's, never the placeholder's ───────────────────────
 * There is no `aria-label` fallback here at all. `Select` carries one because
 * it had already shipped without a name; a NEW control does not get to inherit
 * that debt, and a placeholder promoted to a name is how two controls on one
 * page end up announced identically (SLM-MOONBASE #139, pinned in
 * `Select.name.test.tsx`).
 */

/** One navigable line in the popup. The empty choice is a row like any other. */
type Row =
  | { kind: 'clear'; key: string; label: string }
  | { kind: 'option'; key: string; option: DecoratedOption; open: boolean }

export interface OptionPickerProps {
  /** The stored value. `null` / `''` is EMPTY, which is a normal state. */
  value?: string | null
  /**
   * The chosen value. Clearing emits `''` — the empty STRING, not `null`, so a
   * caller has one type to handle and `resolveOption` reads both as empty.
   */
  onChange: (next: string) => void
  options: readonly DecoratedOption[]
  /** Shown when nothing is chosen. NEVER used as the accessible name. */
  placeholder?: string
  /**
   * Present ⇒ the popup offers an explicit EMPTY choice with this wording.
   * Absent ⇒ it does not. A required field must not offer a way to empty
   * itself, and "is this field clearable" is the caller's question rather than
   * a property of the option set.
   */
  clearLabel?: string
  disabled?: boolean
  /** Lands on the INPUT — the node a user focuses and a test drives. */
  testId?: string
  /* `<Field>` clones these onto its single child (UX-04). They go on the input
     rather than the wrapper, because `id` has to land where `<label htmlFor>`
     can find it. */
  id?: string
  'aria-label'?: string
  'aria-labelledby'?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean
  'aria-required'?: boolean
}

export function OptionPicker({
  value,
  options,
  onChange,
  placeholder = 'Select...',
  clearLabel,
  disabled,
  testId,
  id,
  'aria-label': ariaLabel,
  'aria-labelledby': labelledBy,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
  'aria-required': required,
}: OptionPickerProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [highlight, setHighlight] = useState(0)
  const listId = `${useId()}-list`

  /** What the record currently holds, resolved once for the field and the list. */
  const resolved = resolveOption(value ?? null, options)

  const rows = useMemo<Row[]>(() => {
    const out: Row[] = []
    // The empty choice is filtered like every other line: type three letters of
    // an option and a "None" row lingering at the top is noise.
    if (clearLabel !== undefined && matchesText(query, clearLabel)) {
      out.push({ kind: 'clear', key: 'row:clear', label: clearLabel })
    }
    for (const option of options) {
      // ONE predicate, `textSearch.ts`'s. Every token may occur anywhere, tokens
      // may span the label and the key, and accents do not make a displayed
      // label undiscoverable. A private copy is how two surfaces end up
      // disagreeing about what "matches" means.
      if (!matchesText(query, option.label, option.value)) continue
      out.push({ kind: 'option', key: `opt:${option.value}`, option, open: isSelectable(option) })
    }
    return out
  }, [clearLabel, options, query])

  // Clamped rather than reset, so a shrinking result list can never point
  // `aria-activedescendant` at an id that is no longer rendered.
  const active = rows.length === 0 ? -1 : Math.min(highlight, rows.length - 1)
  const activeId = active >= 0 ? `${listId}-option-${active}` : undefined

  const show = (next: boolean) => {
    setOpen(next)
    if (!next) {
      setQuery('')
      return
    }
    // Opening lands on the current value when there is one, so the first arrow
    // key moves from where the user already is rather than from the top.
    const at = rows.findIndex((row) => row.kind === 'option' && row.option.value === value)
    setHighlight(at >= 0 ? at : 0)
  }

  const choose = (row: Row | undefined) => {
    if (!row) return
    if (row.kind === 'clear') {
      onChange('')
      show(false)
      return
    }
    // An ARCHIVED option is present and labelled, and closed to a NEW choice.
    // The refusal lives here rather than on the element so Enter and click take
    // the same path — a control that refuses on click and accepts on Enter is
    // worse than one that refuses nowhere.
    if (!row.open) return
    onChange(row.option.value)
    show(false)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (!open) show(true)
      else setHighlight(Math.min(rows.length - 1, active + 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      if (!open) show(true)
      else setHighlight(Math.max(0, active - 1))
    } else if (event.key === 'Home' && open) {
      event.preventDefault()
      setHighlight(0)
    } else if (event.key === 'End' && open) {
      event.preventDefault()
      setHighlight(rows.length - 1)
    } else if (event.key === 'Enter') {
      if (!open) return
      event.preventDefault()
      choose(rows[active])
    } else if (event.key === 'Escape') {
      // Closes; it does NOT clear the value. Escape out of a filter must not
      // silently unset the field the filter was searching.
      if (open) {
        event.stopPropagation()
        show(false)
      }
    } else if (event.key === 'Tab') {
      if (open) show(false)
    }
  }

  return (
    <div
      className={s.wrap}
      data-testid={testId ? `${testId}-wrap` : undefined}
      data-open={open || undefined}
      data-option-state={resolved && !resolved.known ? 'unrecognised' : undefined}
    >
      <div className={s.field}>
        {resolved?.known && resolved.icon && (
          <EntityIcon iconKey={resolved.icon} size={15} className={s.fieldIcon} />
        )}
        <input
          className={s.input}
          data-testid={testId}
          type="text"
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-controls={listId}
          aria-activedescendant={open ? activeId : undefined}
          data-tone={resolved?.known ? resolved.tone : undefined}
          id={id}
          aria-label={ariaLabel}
          aria-labelledby={labelledBy}
          aria-describedby={describedBy}
          aria-invalid={invalid}
          aria-required={required}
          disabled={disabled}
          placeholder={placeholder}
          /* Closed, the field READS as the chosen value; open, it is the query.
             An unrecognised stored value contributes no label — the marker
             beside it says so in words instead, because a raw storage key
             rendered where a label goes is exactly row 3's defect. */
          value={open ? query : resolved?.known ? resolved.label : ''}
          onChange={(event) => {
            setQuery(event.target.value)
            setOpen(true)
            setHighlight(0)
          }}
          onFocus={() => { if (!open) show(true) }}
          onClick={() => { if (!open) show(true) }}
          onKeyDown={onKeyDown}
          /* On the INPUT, not on the wrapper. The option buttons `preventDefault`
             their mousedown, so focus never leaves this element while the user
             is inside the control: a real blur therefore means they have left,
             and the popup closes. Putting it on the wrapper instead would make a
             plain `<div>` an interactive element for no gain (jsx-a11y). */
          onBlur={() => show(false)}
        />
        {!open && resolved && !resolved.known && (
          <span className={s.unknown}>
            Not a registered option
            <code className={s.raw}>{resolved.label}</code>
          </span>
        )}
        {!open && resolved?.archived && <span className={s.marker}>{UNAVAILABLE_MARKER}</span>}
        <ChevronDown size={15} className={s.caret} aria-hidden />
      </div>

      {open && (
        <div className={s.menu}>
          {/* The listbox owns OPTIONS and nothing else. The empty-result line is
              a SIBLING of it, not a child — see the header. */}
          <div className={s.list} id={listId} role="listbox">
            {rows.map((row, index) => {
              const selected = row.kind === 'clear' ? resolved === null : row.option.value === value
              const closed = row.kind === 'option' && !row.open
              return (
                <button
                  key={row.key}
                  type="button"
                  id={`${listId}-option-${index}`}
                  role="option"
                  aria-selected={selected}
                  /* `aria-disabled`, not the `disabled` attribute: the row stays
                     a live `aria-activedescendant` target and still reports its
                     state. `choose` is what actually refuses, for both routes. */
                  aria-disabled={closed || undefined}
                  className={cx(s.opt, index === active && s.optHi)}
                  data-hi={index === active || undefined}
                  data-unavailable={closed || undefined}
                  ref={(el) => {
                    if (index === active && el) el.scrollIntoView({ block: 'nearest' })
                  }}
                  onMouseMove={() => { if (index !== active) setHighlight(index) }}
                  /* Keeps focus on the input, so `aria-activedescendant` remains
                     the thing that says where the user is. */
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => choose(row)}
                >
                  <span className={s.tick}>{selected ? <Check size={14} /> : null}</span>
                  {row.kind === 'option' && row.option.icon && (
                    <EntityIcon iconKey={row.option.icon} size={14} className={s.optIcon} />
                  )}
                  <span
                    className={s.optLabel}
                    data-tone={row.kind === 'option' ? normalizeTone(row.option.tone) : 'neutral'}
                  >
                    {row.kind === 'clear' ? row.label : row.option.label}
                  </span>
                  {closed && <span className={s.marker}>{UNAVAILABLE_MARKER}</span>}
                </button>
              )
            })}
          </div>
          {rows.length === 0 && (
            /* An empty RESULT is not an empty option set, and a popup that just
               goes blank leaves the reader unable to tell which they are in. */
            <div className={s.none} data-testid={testId ? `${testId}-no-match` : undefined}>
              Nothing matches &ldquo;{query}&rdquo;.
            </div>
          )}
        </div>
      )}
    </div>
  )
}
